import PropTypes from "prop-types";

import { sendSlackNotification } from "./notifications";
import { countryCodeList } from "./sharedDatasets";

const _lead_iso_countries = Object.freeze({
  en: "UK",
  us: "US",
  au: "AUS",
  za: "ZA",
  can: "CAN",
  ie: "EUR"
});

const _lead_shortcodes = Object.freeze({
  application: `GVI core LOCALE_SITE application form`,
  application_form: `GVI core LOCALE_SITE application form`,
  ingo_application_form: `GVI Flex LOCALE_SITE application form`,
  program_testimonial: `GVI core LOCALE_SITE inquiry form`,
  complete_application: `GVI core LOCALE_SITE application form`,
  gvionline_application_form: `GVI ONLINE LOCALE_SITE application form`,
  gvivirtual_application_form: `GVI VIRTUAL LOCALE_SITE application form`,
  gvivirtual_career_application_form: `GVI VIRTUAL Career LOCALE_SITE application form`,
  gvivirtual_uncc_application_form: `UNCC Virtual Internship application form`,
  enquiry_form: `GVI core LOCALE_SITE inquiry form`,
  ingo_enquiry_form: `GVI Flex LOCALE_SITE inquiry form`,
  gvionline_enquiry_form: `GVI ONLINE LOCALE_SITE inquiry form`,
  gvivirtual_enquiry_form: `GVI VIRTUAL LOCALE_SITE inquiry form`,
  gvivirtual_career_enquiry_form: `GVI VIRTUAL Career LOCALE_SITE inquiry form`,
  gvivirtual_uncc_enquiry_form: `UNCC Virtual inquiry form`,
  callback_form: `GVI core LOCALE_SITE call back form`,
  brochures_form: `GVI core LOCALE_SITE brochure form`,
  ingo_brochures_form: `GVI Flex LOCALE_SITE brochure form`,
  journeys_brochures_form: `GVI Journeys LOCALE_SITE brochure form`,
  journeys_application_form: `GVI Journeys LOCALE_SITE application form`,
  journeys_enquiry_form: `GVI Journeys LOCALE_SITE inquiry form`,
  newsletter_form: `GVI core LOCALE_SITE newsletter form`,
  group_school_enquiry: `GVI core LOCALE_SITE school parent form`,
  group_enquiry_form: `GVI core LOCALE_SITE Group Inquiry`,
  group_callback_form: `GVI core LOCALE_SITE Group call back form`,
  group_application_form: `GVI core LOCALE_SITE Group application form`,
  group_alumni_request: `GVI Core LOCALE_SITE Group Speak to Alumni form`,
  core_uk_affinity_enquiry: `GVI core LOCALE_SITE Affiliate Inquiry`,
  core_ayp_enquiry: `GVI core LOCALE_SITE AYP Inquiry`,
  sl_enquiry_form: `GVI Core LOCALE_SITE SL Inquiry`,
  sl_callback_form: `GVI Core LOCALE_SITE SL call back form`,
  meetup: `GVI Core LOCALE_SITE meetup form`,
  openday: `GVI Core LOCALE_SITE Open Day form`,
  webinar: `GVI Core LOCALE_SITE Webinar form`,
  openday_application_form: `GVI Core LOCALE_SITE Open Day application form`,
  parent_request: `GVI Core LOCALE_SITE parent form`,
  alumni_request: `GVI Core LOCALE_SITE Speak to Alumni form`,
  writing_academy: `GVI Core LOCALE_SITE Writing Academy form`,
  gvionline_brochures_form: `GVI Core LOCALE_SITE Online Course Download`,
  vi_project_form: `GVI Core LOCALE_SITE VI Projects Download`,
  callback_form_raleigh: `GVI Core LOCALE_SITE Raleigh Promo call back form `,
  enquiry_form_raleigh: `GVI Core LOCALE_SITE Raleigh Promo inquiry form`,
  callback_form_consultation: `GVI Core LOCALE_SITE Free Consultation call back form `,
  enquiry_form_consultation: `GVI Core LOCALE_SITE Free Consultation inquiry form`,
  itinerary_form: `GVI Core LOCALE_SITE Itinerary download form`,
  itinerary_groups_form: `GVI Core LOCALE_SITE Groups Itinerary download form`,
  itinerary_under18_form: `GVI Core LOCALE_SITE Under18 Itinerary download form`,
  outreach_form: `GVI Core LOCALE_SITE Outreach form`
});

export const processForm = ({
  formdata,
  planetDomain,
  currentBrand,
  peopleDomain,
  localeLanguage,
  cookies = {}
}) => {
  const suffixTitle = _lead_iso_countries[localeLanguage];

  //formtype based on locale
  const form_shortcode = (
    formdata?.shortcode ??
    _lead_shortcodes[formdata?.form_type ?? "enquiry_form"]
  )?.replace("LOCALE_SITE", suffixTitle);

  let pageViewCounter = [];
  let pageViewCount = 0;
  let datesCostsViewed = false;
  let currentUrl = "";
  let modifiedCookies = {};
  let privacyMode = "";

  if (typeof window !== "undefined") {
    const storedCounter = window?.localStorage.getItem("pageViewCounter");
    try {
      pageViewCounter = storedCounter ? JSON.parse(storedCounter) : [];
    } catch (error) {
      console.error("Error parsing pageViewCounter from localStorage: ", error);
      pageViewCounter = [];
    }

    const storedCount = window?.localStorage.getItem("pageViewCount");
    pageViewCount = storedCount ? parseInt(storedCount) : 0;

    const storedDatesCostsViewed =
      window?.localStorage.getItem("datesCostsViewed");
    datesCostsViewed = storedDatesCostsViewed === "true";

    // eslint-disable-next-line no-restricted-globals
    currentUrl = new URL(location ?? window?.location);

    const firstSession = JSON.parse(
      window?.localStorage.getItem("FirstSession") || "{}"
    );
    const returningSession = JSON.parse(
      window?.localStorage.getItem("ReturningSession") || "{}"
    );
    const analytics = JSON.parse(window?.localStorage.getItem("gvx-analytics"));

    privacyMode =
      returningSession?.uri?.length > 1 || firstSession?.uri?.length > 1
        ? "privacy_mode"
        : "";

    const { country, device, timezone, region, city } = analytics || {};

    // console.debug("analytics - details", analytics);

    modifiedCookies = {
      cookieless: true,
      FirstSession: firstSession || {},
      ReturningSession: returningSession || {},
      gclid: returningSession?.gclid ?? firstSession?.gclid ?? "",
      wbraid: returningSession?.wbraid ?? firstSession?.wbraid ?? "",
      gbraid: returningSession?.gbraid ?? firstSession?.gbraid ?? "",
      "gvx-analytics-ip": analytics?.ip || "",
      "gvx-analytics-country-code": country || "",
      "gvx-analytics-country-name":
        countryCodeList[country?.toLowerCase()] || "",
      "gvx-analytics-device": device || "desktop",
      "gvx-analytics-timezone": timezone || "",
      "gvx-analytics-region": region || "",
      "gvx-analytics-city": city || "",
      user_journey: JSON.parse(
        window.localStorage.getItem("pageViewInteraction")
      ),
      page_view_count: JSON.parse(window.localStorage.getItem("pageViewCount")),
      ...analytics
    };
  }

  const gclid = modifiedCookies?.gclid ?? cookies.gclid ?? "";
  const wbraid = modifiedCookies?.wbraid ?? cookies.wbraid ?? "";
  const gbraid = modifiedCookies?.gbraid ?? cookies.gbraid ?? "";

  // console.debug("cookies comparison", { cookies, modifiedCookies });

  // return true;
  const prepareFormData = {
    source: "API",
    csrf_token: "gvi.manual.retry",
    form_shortcode: form_shortcode,
    gclid: gclid,
    gbraid: gbraid,
    wbraid: wbraid,
    privacy_mode: privacyMode,
    dates_costs: datesCostsViewed === true ? true : false,
    multiple_pages: pageViewCount > 5 ? true : false,
    cookies: modifiedCookies ?? cookies,
    referer_url: currentUrl?.href || "",
    ...formdata
  };

  // console.debug("processForm :: repareFormData", prepareFormData);

  // return;
  return sendForm({
    formFields: prepareFormData,
    planetDomain,
    currentBrand,
    peopleDomain
  });
};

export const sendForm = async ({
  formFields,
  planetDomain,
  currentBrand,
  peopleDomain
}) => {
  const apiEndpoint = currentBrand?.includes("people")
    ? peopleDomain
    : planetDomain;

  // console.debug("sendForm :: apiEndpoint", {
  //   apiEndpoint,
  //   formFields,
  //   planetDomain,
  //   currentBrand,
  //   peopleDomain
  // });

  const apiUrl = [
    apiEndpoint,
    "/wp-json/gvi/v1/process_form",
    "?_",
    new Date().getTime()
  ].join("");

  let apiPath = ["/api/gvi/processform", "?_", new Date().getTime()].join("");

  const formBodyData = { ...formFields, apiUrl: apiUrl };

  return await fetch(apiPath, {
    method: `POST`,
    mode: "cors",
    cache: "no-cache",
    body: JSON.stringify(formBodyData),
    headers: {
      "content-type": `application/json`
    }
  })
    .then((res) => res.json())
    .catch(async (err) => {
      //call fallback api
      let apiFallbackPath = [
        "/api/gvi/processformfallback",
        "?_",
        new Date().getTime()
      ].join("");

      return await fetch(apiFallbackPath, {
        method: `POST`,
        mode: "cors",
        cache: "no-cache",
        body: JSON.stringify(formBodyData),
        headers: {
          "content-type": `application/json`
        }
      })
        .then((res) => res.json())
        .catch(async (err) => {
          //call legacy api
          return await fetch(apiUrl, {
            method: `POST`,
            mode: "cors",
            cache: "no-cache",
            body: JSON.stringify(formFields),
            headers: {
              "content-type": `application/json`
            }
          })
            .then((res) => res.json())
            .catch((err) => {
              const errMessage = {
                error: JSON.stringify(err) || err,
                api_url: apiUrl,
                current_brand: currentBrand,
                form_fields_data: formFields
              };

              sendSlackNotification({
                message: errMessage,
                title: "processForm Legacy API Fallback Error"
              });

              return { error: errMessage, success: false };
            });
        });
    });
};

processForm.propTypes = {
  formdata: PropTypes.object.isRequired,
  currentBrand: PropTypes.string.isRequired,
  planetDomain: PropTypes.string.isRequired,
  peopleDomain: PropTypes.string.isRequired,
  localeLanguage: PropTypes.string.isRequired
};

sendForm.propTypes = {
  formFields: PropTypes.object.isRequired,
  currentBrand: PropTypes.string.isRequired,
  planetDomain: PropTypes.string.isRequired,
  peopleDomain: PropTypes.string.isRequired,
  localeLanguage: PropTypes.string.isRequired
};
